import type {
	IAuthRepository,
	AuthLoginResponse,
	AuthTokenResponse,
} from "@/application/ports/repositories";
import type { User, UserRole, UserStatus } from "@/domain/user";
import { apiClient, type ApiResponse } from "../client";
import { getLocalStorageService } from "@/infrastructure/di/services";
import { STORAGE_KEYS } from "@/infrastructure/storage/constants";

// Private backend response types - implementation detail
interface BackendAuthResponse {
	access_token: string;
	refresh_token: string;
	expires_in: number;
	token_type: string;
}

interface BackendUserProfile {
	uuid: string;
	email: string;
	first_name: string | null;
	last_name: string | null;
	full_name: string;
	profile_picture_signed_url: string | null;
	is_active: boolean;
	is_verified: boolean;
	is_platform_admin: boolean;
	created_at: string;
	updated_at: string;
	last_login_at: string | null;
}

export class AuthRepository implements IAuthRepository {
	private authPath = "/auth";

	async login(email: string, password: string): Promise<AuthLoginResponse> {
		const response: ApiResponse<BackendAuthResponse> = await apiClient.post(
			`${this.authPath}/login`,
			{
				email,
				password,
			},
		);

		const { access_token, refresh_token, expires_in } = response.data;

		// Get user profile immediately after login
		const user = await this.getUserProfile(access_token);

		return {
			tokens: {
				accessToken: access_token,
				refreshToken: refresh_token,
				expiresIn: expires_in,
			},
			user,
		};
	}

	async logout(refreshToken: string): Promise<void> {
		await apiClient.post(`${this.authPath}/logout`, {
			refresh_token: refreshToken,
		});
	}

	async refreshToken(refreshToken: string): Promise<AuthTokenResponse> {
		const response: ApiResponse<BackendAuthResponse> = await apiClient.post(
			`${this.authPath}/refresh`,
			{ refresh_token: refreshToken },
		);

		const { access_token, refresh_token, expires_in } = response.data;

		return {
			accessToken: access_token,
			refreshToken: refresh_token,
			expiresIn: expires_in,
		};
	}

	async getUserProfile(accessToken: string): Promise<User> {
		const storageService = getLocalStorageService();
		// Temporarily set token for this request
		const originalToken = storageService.get<string>(STORAGE_KEYS.AUTH_TOKEN);
		storageService.set(STORAGE_KEYS.AUTH_TOKEN, accessToken);

		try {
			const response: ApiResponse<BackendUserProfile> =
				await apiClient.get("/users/me");
			return this.mapBackendUserToUser(response.data);
		} finally {
			// Restore original token
			if (originalToken) {
				storageService.set(STORAGE_KEYS.AUTH_TOKEN, originalToken);
			} else {
				storageService.remove(STORAGE_KEYS.AUTH_TOKEN);
			}
		}
	}

	async forgotPassword(email: string): Promise<void> {
		await apiClient.post(`${this.authPath}/forgot-password`, {
			email: email.trim(),
		});
	}

	async resetPassword(token: string, newPassword: string): Promise<void> {
		await apiClient.post(`${this.authPath}/reset-password`, {
			token: token.trim(),
			new_password: newPassword,
		});
	}

	private mapBackendUserToUser(backendUser: BackendUserProfile): User {
		const fullName = backendUser.full_name || backendUser.email;
		const role: UserRole = "viewer";
		const status: UserStatus = backendUser.is_active ? "active" : "inactive";

		return {
			id: backendUser.uuid,
			name: fullName,
			email: backendUser.email,
			role,
			status,
			joinDate: new Date(backendUser.created_at),
			avatar: backendUser.profile_picture_signed_url || undefined,
			profile_picture_signed_url:
				backendUser.profile_picture_signed_url || undefined,
			first_name: backendUser.first_name,
			last_name: backendUser.last_name,
			is_verified: backendUser.is_verified,
			last_login_at: backendUser.last_login_at,
			uuid: backendUser.uuid,
		};
	}
}
